import dayjs from 'dayjs';

import { EMainProductCodeType } from '@webtrader/core/api/config/company';
import { TRADE_FILL_POLICY_TEXT_FN_MAP } from '@webtrader/core/constants/order';
import { getSecondsSinceEpoch } from '@webtrader/core/dayjs';
import { mapOrderTypeToText } from '@webtrader/core/format/order';
import { kSeparatorForNumberOrDecimals } from '@webtrader/core/format/price';
import { convertToDisplayUTC } from '@webtrader/core/hooks/timezone/useDisplayUTCTime';
import { TMixpanelProperties } from '@webtrader/core/mix-panel/types';
import { EMixpanelProperties } from '@webtrader/core/services/analysis/constants';
import placeOrderHelper from '@webtrader/core/services/analysis/helpers/PlaceOrderHelper';
import userHelper from '@webtrader/core/services/analysis/helpers/UserHelper';
import { companyService } from '@webtrader/core/services/company';
import { orderService } from '@webtrader/core/services/order';

import {
  ICloseOrderProperties,
  ICreateBulkCloseOpenPendingPropertiesProps,
  ICreateBulkCloseOpenPendingSubmittedPropertiesProps,
  IDeleteOrderProperties
} from './type';

class CloseOrderHeler {
  public createBulkCloseOpenPendingSubmittedProperties = ({
    symbolDetails,
    numOfPositions,
    bulkCloseType
  }: ICreateBulkCloseOpenPendingSubmittedPropertiesProps): TMixpanelProperties => {
    const tradePreference = placeOrderHelper.createTradePreferenceProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    const { symbol, currency } = symbolDetails || {};
    return {
      ...tradePreference,
      [EMixpanelProperties.BULK_CLOSE_TYPE]: bulkCloseType,
      [EMixpanelProperties.NUM_OF_POSITIONS_ALL]: numOfPositions ?? 0,
      [EMixpanelProperties.SYMBOL]: symbol,
      [EMixpanelProperties.SYMBOL_CURRENCY]: currency,
      ...userAccountProperties
    };
  };

  public createBulkCloseOpenPendingProperties = ({
    symbol,
    numOfPositions,
    bulkCloseType
  }: ICreateBulkCloseOpenPendingPropertiesProps): TMixpanelProperties => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    return {
      [EMixpanelProperties.SYMBOL]: symbol,
      [EMixpanelProperties.NUM_OF_POSITIONS_ALL]: numOfPositions ?? 0,
      [EMixpanelProperties.BULK_CLOSE_TYPE]: bulkCloseType,
      ...userAccountProperties
    };
  };

  // eslint-disable-next-line complexity
  private closeOrderProperties = ({ values, order, livePrice }: ICloseOrderProperties): TMixpanelProperties => {
    const profitTrackingOrders = orderService.getProfitTrackingOrders();
    const currentOrderWithLiveProfit = profitTrackingOrders.find((o) => o.orderId === order.orderId);
    const profitLoss = currentOrderWithLiveProfit?.profit;

    const { lotSize, fillPolicy } = values;

    const {
      symbol,
      currency,
      openTime,
      orderType,
      units,
      openPrice,
      currentPrice,
      takeProfit,
      stopLoss,
      swap,
      commission,
      lotSize: maxLotSize
    } = order;

    const convertedOpenTime = getSecondsSinceEpoch(
      companyService.convertToDayjsWithOffset({ input: openTime, keepTime: true })
    );

    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.SYMBOL_CURRENCY]: currency ?? '-',
      [EMixpanelProperties.OPEN_TIME]: openTime
        ? convertToDisplayUTC(dayjs(convertedOpenTime).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.ORDER_TYPE]: orderType ? mapOrderTypeToText(orderType) : '-',
      [EMixpanelProperties.LOT_SIZE]: maxLotSize ? kSeparatorForNumberOrDecimals(maxLotSize) : '-',
      [EMixpanelProperties.VOLUME_SIZE_SUBMITTED]: lotSize ? kSeparatorForNumberOrDecimals(lotSize) : '-',
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? TRADE_FILL_POLICY_TEXT_FN_MAP[fillPolicy]() : '-',
      [EMixpanelProperties.OPEN_PRICE]: openPrice ? kSeparatorForNumberOrDecimals(openPrice) : '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? kSeparatorForNumberOrDecimals(livePrice) : currentPrice ?? '-',
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ? kSeparatorForNumberOrDecimals(takeProfit) : '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ? kSeparatorForNumberOrDecimals(stopLoss) : '-',
      [EMixpanelProperties.SWAP]: swap ? kSeparatorForNumberOrDecimals(swap) : '-',
      [EMixpanelProperties.COMMISSION]: commission ? kSeparatorForNumberOrDecimals(commission) : '-',
      [EMixpanelProperties.UNITS]: units ? kSeparatorForNumberOrDecimals(units) : '-',
      [EMixpanelProperties.PROFIT_LOSS]: profitLoss ? kSeparatorForNumberOrDecimals(profitLoss) : '-'
    };
  };

  public createCloseOrderSubmittedProperties = ({
    values,
    order,
    livePrice
  }: ICloseOrderProperties): TMixpanelProperties => {
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    const closeOrderProperties = this.closeOrderProperties({ values, order, livePrice });
    const userAccountProperties = userHelper.createUserAccountProperties();
    return {
      ...closeOrderProperties,
      ...tradePreferenceProperties,
      ...userAccountProperties
    };
  };

  // eslint-disable-next-line complexity
  private deleteOrderProperties = ({ order, livePrice }: IDeleteOrderProperties): TMixpanelProperties => {
    const {
      lotSize,
      fillPolicy,
      symbol,
      openTime,
      orderType,
      units,
      expiration,
      tradeExpiry,
      price, // Entry Price
      priceTrigger, // Stop Limit Price / Pending Price
      currentPrice,
      takeProfit,
      stopLoss,
      filledLotSize
    } = order;

    const isMT5 = companyService.getIntegration() === EMainProductCodeType.METATRADER5;

    const displayLotSize = () => {
      const filled = Number.isFinite(lotSize) ? kSeparatorForNumberOrDecimals(lotSize) : '-';

      if (isMT5) {
        const unfill = Number.isFinite(filledLotSize) ? kSeparatorForNumberOrDecimals(filledLotSize) : '-';
        return `${filled} / ${unfill}`;
      }

      return filled;
    };

    const convertedOpenTime = getSecondsSinceEpoch(
      companyService.convertToDayjsWithOffset({ input: openTime, keepTime: true })
    );

    const convertedExpiration = getSecondsSinceEpoch(
      companyService.convertToDayjsWithOffset({ input: expiration, keepTime: true })
    );

    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.OPEN_TIME]: openTime
        ? convertToDisplayUTC(dayjs(convertedOpenTime).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.ORDER_TYPE]: orderType ? mapOrderTypeToText(orderType) : '-',
      [EMixpanelProperties.LOT_SIZE]: displayLotSize() ?? '-',
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? TRADE_FILL_POLICY_TEXT_FN_MAP[fillPolicy]() : '-',
      [EMixpanelProperties.EXPIRATION]: expiration
        ? convertToDisplayUTC(dayjs(convertedExpiration).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.TRADE_EXPIRY]: tradeExpiry ? tradeExpiry : '-',
      [EMixpanelProperties.PRICE]: price ? kSeparatorForNumberOrDecimals(price) : '-',
      [EMixpanelProperties.PENDING_PRICE]: priceTrigger ? kSeparatorForNumberOrDecimals(priceTrigger) : '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? kSeparatorForNumberOrDecimals(livePrice) : currentPrice ?? 0,
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ? kSeparatorForNumberOrDecimals(takeProfit) : '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ? kSeparatorForNumberOrDecimals(stopLoss) : '-',
      [EMixpanelProperties.UNITS]: units ? kSeparatorForNumberOrDecimals(units) : '-'
    };
  };

  public createDeleteOrderSubmittedProperties = ({ order, livePrice }: IDeleteOrderProperties): TMixpanelProperties => {
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    const deleteOrderProperties = this.deleteOrderProperties({ order, livePrice });
    const userAccountProperties = userHelper.createUserAccountProperties();
    return {
      ...deleteOrderProperties,
      ...tradePreferenceProperties,
      ...userAccountProperties
    };
  };
}

const closeOrderHeler = new CloseOrderHeler();
export default closeOrderHeler;
